import { describe_error } from '../utils/ErrorDescriber';
import React, { useState, useEffect, useRef } from 'react';
import { Form, Button, Spinner, Alert } from 'react-bootstrap';
import axios from 'axios';

import Constants from '../Constants';
import { useAuth } from '../AuthContext';
import { get_user_error_message } from '../utils/ErrorMessages';
import { get_status_label, get_user_name, format_date } from '../components/superadmin/UserFormat';
import { c_SessionsNotEndedText } from '../components/superadmin/UsersTable';

// The user's fields in the order shown, with their labels. Any other field the backend sends follows them under its
// own name, so every detail of the user is on the page.
const c_UserFields = [
	{ key: 'id', label: 'User id' },
	{ key: 'email', label: 'Email' },
	{ key: 'firstname', label: 'First name' },
	{ key: 'lastname', label: 'Last name' },
	{ key: 'phone', label: 'Phone' },
	{ key: 'email_verified', label: 'Email verified' },
	{ key: 'lastlogin', label: 'Last login' },
	{ key: 'createdAt', label: 'Signed up' },
	{ key: 'updatedAt', label: 'Last updated' },
	{ key: 'ip_address', label: 'IP address' },
	{ key: 'browser_uuid', label: 'Browser id' },
	{ key: 'username', label: 'Username' },
	{ key: 'user_group_id', label: 'User group id' },
	{ key: 'client_id', label: 'Client id' },
	{ key: 'organizationid', label: 'Organization id' },
	{ key: 'pricingtierid', label: 'Pricing tier id' },
	{ key: 'addressid', label: 'Address id' }
];

// The fields shown elsewhere on the page, not in the list.
const c_FieldsShownElsewhere = ['status'];

// The fields that are dates.
const c_DateFields = ['lastlogin', 'createdAt', 'updatedAt'];

// Shown for a field the user has no value for.
const c_NoValueText = 'None';

// Shown when the invite has been sent again.
const c_InviteResentText = 'The invite has been sent again.';

// One user in full, for the superadmin page, laid out as the Account page (ProfileView) is: one card of sections,
// each a titled block of label and value rows. The sections: their status (a dropdown that changes it), their
// invite while it is pending, every field of their record, their subscription, and how many sites and analyses they
// have. on_back returns to the users table.
function UserView({user_id, on_back}){
	const { token } = useAuth();

	const [details, set_details] = useState(null);
	const [load_error, set_load_error] = useState('');
	const [status_error, set_status_error] = useState('');
	const [saving_status, set_saving_status] = useState(false);

	// The invite being sent again, and how it went: the success text or the error, one at a time.
	const [resending_invite, set_resending_invite] = useState(false);
	const [resend_success, set_resend_success] = useState('');
	const [resend_error, set_resend_error] = useState('');

	const mounted_ref = useRef(true);

	const get_headers = ()=>{
		return { Authorization: 'Bearer ' + token };
	};

	const load_user = async()=>{
		set_load_error('');

		try {
			const response = await axios.get(Constants.SERVER_URL + '/api/superadmin/user/' + user_id, { headers: get_headers() });
			if(mounted_ref.current){
				set_details(response.data);
			}
		}
		catch (error) {
			console.log('UserView/load_user - ERROR: ', describe_error(error));
			if(mounted_ref.current){
				set_load_error(get_user_error_message(error, 'The user could not be loaded.'));
				set_details(null);
			}
		}
	};

	useEffect(()=>{
		mounted_ref.current = true;
		set_details(null);
		load_user();

		return ()=>{
			mounted_ref.current = false;
		};
	}, [token, user_id]);

	const change_status = async(status)=>{
		if(status === details.user.status){
			return;
		}

		set_saving_status(true);
		set_status_error('');

		try {
			const response = await axios.put(Constants.SERVER_URL + '/api/superadmin/user/' + user_id + '/status', { status }, { headers: get_headers() });
			if(mounted_ref.current){
				set_details({ ...details, user: response.data.user });
				if(response.data.tokens_revoked === false){
					set_status_error(c_SessionsNotEndedText);
				}
			}
		}
		catch (error) {
			console.log('UserView/change_status - ERROR: ', describe_error(error));
			if(mounted_ref.current){
				set_status_error(get_user_error_message(error, 'The status could not be changed.'));
			}
		}

		if(mounted_ref.current){
			set_saving_status(false);
		}
	};

	const resend_invite = async()=>{
		set_resending_invite(true);
		set_resend_success('');
		set_resend_error('');

		try {
			await axios.post(Constants.SERVER_URL + '/api/superadmin/user/' + user_id + '/resend_invite', {}, { headers: get_headers() });
			if(mounted_ref.current){
				set_resend_success(c_InviteResentText);
			}
		}
		catch (error) {
			console.log('UserView/resend_invite - ERROR: ', describe_error(error));
			if(mounted_ref.current){
				set_resend_error(get_user_error_message(error, 'The invite could not be sent again.'));
			}
		}

		if(mounted_ref.current){
			set_resending_invite(false);
		}
	};

	// A field's value as shown: dates in the locale, booleans as Yes or No, nothing as None.
	const format_value = (key, value)=>{
		if((value === null) || (value === undefined) || (value === '')){
			return c_NoValueText;
		}

		if(c_DateFields.includes(key)){
			return format_date(value);
		}

		if(typeof value === 'boolean'){
			return value ? 'Yes' : 'No';
		}

		if(typeof value === 'object'){
			return JSON.stringify(value);
		}

		return String(value);
	};

	// The fields to list: the known ones in order, then any other the record holds.
	const get_fields = (user)=>{
		const fields = c_UserFields.filter((field)=>{ return field.key in user; });
		const listed_keys = fields.map((field)=>{ return field.key; }).concat(c_FieldsShownElsewhere);
		Object.keys(user).forEach((key)=>{
			if(!listed_keys.includes(key)){
				fields.push({ key, label: key });
			}
		});

		return fields;
	};

	// One label and value row, as the Account page shows a field.
	const render_field = (field, value)=>{
		return (
			<div key={field.key} className="profile-field user-view-field">
				<span className="profile-field-label user-view-field-label">{field.label}</span>
				<span className="profile-field-value">{format_value(field.key, value)}</span>
			</div>
		);
	};

	const render_status_section = ()=>{
		const { user, statuses } = details;
		return (
			<div className="profile-section">
				<div className="profile-setting-title-row">
					<h5 className="profile-section-title">Status</h5>
					{saving_status && <Spinner animation="border" size="sm"/>}
				</div>
				<div className="user-view-status-row">
					<Form.Select
						className="users-status-select"
						value={user.status ? user.status : ''}
						disabled={saving_status}
						onChange={(event)=>{ change_status(event.target.value); }}>
						{statuses.map((status)=>{
							return <option key={status} value={status}>{get_status_label(status)}</option>;
						})}
					</Form.Select>
				</div>
				<p className="profile-field-label profile-setting-description">
					Only an active user can log in. Any other status ends the user's sessions at once.
				</p>
				{status_error && <Alert variant="danger" className="user-view-alert">{status_error}</Alert>}
			</div>
		);
	};

	// Shown only while the user is invite pending: they were invited and have not yet set their password, so the
	// invite can be sent again.
	const render_invite_section = ()=>{
		if(!details.invite_pending){
			return null;
		}

		return (
			<div className="profile-section">
				<h5 className="profile-section-title">Invite</h5>
				<p className="profile-field-label user-view-section-note">
					This user has been invited and has not yet set their password. The invite can be sent again if they did not receive it.
				</p>
				<Button variant="primary" disabled={resending_invite} onClick={resend_invite}>
					{resending_invite ? <Spinner animation="border" size="sm"/> : 'Resend Invite'}
				</Button>
				{resend_success && <Alert variant="success" className="user-view-alert">{resend_success}</Alert>}
				{resend_error && <Alert variant="danger" className="user-view-alert">{resend_error}</Alert>}
			</div>
		);
	};

	const render_details_section = ()=>{
		const { user, is_superadmin, invite_pending } = details;
		return (
			<div className="profile-section">
				<h5 className="profile-section-title">Details</h5>
				{render_field({ key: 'is_superadmin', label: 'Superadmin' }, is_superadmin)}
				{render_field({ key: 'invite_pending', label: 'Invite pending' }, invite_pending)}
				{get_fields(user).map((field)=>{ return render_field(field, user[field.key]); })}
			</div>
		);
	};

	const render_subscription_fields = (subscription)=>{
		return (
			<React.Fragment>
				<div className="profile-sub-plan-row">
					<span className="profile-field-label">Plan</span>
					<span className="profile-field-value profile-field-value-bold">{subscription.plan_name ? subscription.plan_name : c_NoValueText}</span>
				</div>
				{render_field({ key: 'status', label: 'Status' }, get_status_label(subscription.status))}
				{render_field({ key: 'start_date', label: 'Started' }, format_date(subscription.start_date))}
				{render_field({ key: 'renewal_date', label: 'Renews' }, format_date(subscription.renewal_date))}
				{render_field({ key: 'end_date', label: 'Ends' }, format_date(subscription.end_date))}
				{render_field({ key: 'cancel_at_period_end', label: 'Cancels at period end' }, subscription.cancel_at_period_end)}
				{render_field({ key: 'signup_coupon', label: 'Signup coupon' }, subscription.signup_coupon)}
				{render_field({ key: 'plan_id', label: 'Plan id' }, subscription.plan_id)}
				{render_field({ key: 'id', label: 'Subscription id' }, subscription.id)}
			</React.Fragment>
		);
	};

	const render_subscription_section = ()=>{
		const { subscription, subscription_error } = details;

		var body;
		if(subscription_error){
			body = <span className="profile-field-label">The subscription could not be loaded from the subscriptions service.</span>;
		}
		else if(!subscription){
			body = <span className="profile-field-label">No subscription.</span>;
		}
		else {
			body = render_subscription_fields(subscription);
		}

		return (
			<div className="profile-section">
				<h5 className="profile-section-title">Subscription</h5>
				{body}
			</div>
		);
	};

	const render_usage_section = ()=>{
		return (
			<div className="profile-section">
				<h5 className="profile-section-title">Usage</h5>
				{render_field({ key: 'num_sites', label: 'Sites' }, details.num_sites)}
				{render_field({ key: 'num_analyses', label: 'Page analyses' }, details.num_analyses)}
			</div>
		);
	};

	const render_card = ()=>{
		if(load_error){
			return <Alert variant="danger">{load_error}</Alert>;
		}

		if(!details){
			return (
				<div className="superadmin-loading">
					<Spinner animation="border" size="sm"/>
				</div>
			);
		}

		return (
			<div className="profile-card">
				{render_status_section()}
				{render_invite_section()}
				{render_details_section()}
				{render_subscription_section()}
				{render_usage_section()}
			</div>
		);
	};

	const render_header = ()=>{
		const name = details ? get_user_name(details.user) : 'User';
		const shows_email = details && (name !== details.user.email);
		return (
			<div className="profile-header user-view-header">
				<div>
					<h4 className="profile-title">{name}</h4>
					{shows_email && <span className="profile-field-label">{details.user.email}</span>}
				</div>
				<Button variant="primary" onClick={on_back}>
					Back to Users
				</Button>
			</div>
		);
	};

	return (
		<div className="user-view">
			{render_header()}
			{render_card()}
		</div>
	);
}

export default UserView;
